import { CalendarMonth, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { formatWeekRange } from '../../utils/dateUtils';

interface WeekNavigationProps {
  currentWeek: Date;
  onPreviousWeek: () => void;
  onNextWeek: () => void;
  onJumpToToday?: () => void;
  canGoToPrevious: boolean;
  canGoToNext: boolean;
  isCurrentWeek?: boolean;
  weeklyTSS?: number; // Total TSS for the week
  children?: React.ReactNode; // For deload week indicator
}

export const WeekNavigation: React.FC<WeekNavigationProps> = ({
  currentWeek,
  onPreviousWeek,
  onNextWeek,
  onJumpToToday,
  canGoToPrevious,
  canGoToNext,
  isCurrentWeek = false,
  weeklyTSS,
  children,
}) => {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={1.5}
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 1.5,
        p: 1,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 1,
      }}
    >
      <IconButton
        onClick={onPreviousWeek}
        disabled={!canGoToPrevious}
        size="small"
        sx={{
          opacity: canGoToPrevious ? 1 : 0.3,
          '&:hover': {
            transform: 'scale(1.05)',
          },
          transition: 'all 0.3s ease',
        }}
      >
        <ChevronLeft />
      </IconButton>

      <Box display="flex" flexDirection="column" alignItems="center">
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip
            title={onJumpToToday && !isCurrentWeek ? 'Jump to current week' : 'Current week'}
            arrow
            placement="top"
          >
            <span>
              <IconButton
                onClick={onJumpToToday}
                size="small"
                disabled={!onJumpToToday || isCurrentWeek}
                aria-label="jump-to-current-week"
                sx={{
                  color: !onJumpToToday || isCurrentWeek ? 'text.disabled' : 'primary.main',
                  '&:hover': {
                    transform: !onJumpToToday || isCurrentWeek ? 'none' : 'scale(1.1)',
                    backgroundColor: !onJumpToToday || isCurrentWeek ? 'inherit' : 'primary.light',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <CalendarMonth fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography
            variant="h6"
            component="h2"
            onClick={onJumpToToday}
            sx={{
              fontWeight: 500,
              textAlign: 'center',
              color: 'text.primary',
              fontSize: '1.1rem',
            }}
          >
            {formatWeekRange(currentWeek)}
          </Typography>
        </Box>
        {weeklyTSS !== undefined && weeklyTSS > 0 && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.75rem',
              fontWeight: 500,
            }}
          >
            Weekly TSS: {Math.round(weeklyTSS)}
          </Typography>
        )}
        {children}
      </Box>

      <IconButton
        onClick={onNextWeek}
        disabled={!canGoToNext}
        size="small"
        sx={{
          opacity: canGoToNext ? 1 : 0.3,
          '&:hover': {
            transform: 'scale(1.05)',
          },
          transition: 'all 0.3s ease',
        }}
      >
        <ChevronRight />
      </IconButton>
    </Box>
  );
};
